import { useEffect } from "react";
import Draggable from "react-draggable";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import type { FeatureCollection } from "geojson";

import { useGetBulkIsochroneQuery } from "@/api/isochrone/useIsochroneAPI";
import { CloseIcon } from "@/assets/icons";
import { Divider } from "@/components/ui/Divider";
import useSpatialCatchment from "@/hooks/spatialAnalysis/useSpatialCatchment";
import { getIndexOfHighestValue } from "@/lib/utils";
import { RootState } from "@/store";
import {
  setCatchmentSettings,
  setCatchmentSettingsTemp,
  setCatchmentSettingsTempRoad,
  setSpatialConfig
} from "@/store/slice/spatialSlice";
import {
  CatchmentTransport,
  CatchmentType,
  ECatchmentType,
  ERoadAccessMeasurement,
  ERoadExclusions,
  RoadAccessMeasurement
} from "@/types/spatial";
import { getInvalidContours } from "@/utils/isochrone";

import { area, circle, lineString, lineToPolygon } from "@turf/turf";
import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import CatchmentDataSection from "../catchmentDataSection";
import CatchmentTypeSection from "../catchmentTypeSection";

const TEST_ID_PREFIX = "catchment-settings-popup";
interface Props {
  onClose: () => void;
  onOpenDatasetTabularModal?: () => void;
}

export type CatchmentSettingsFormValues = {
  radiusData: string[];
  distances: string[];
  durations: string[];
  catchmentType: CatchmentType;
  measurementType: RoadAccessMeasurement;
  routingType: CatchmentTransport;
  roadExclusions: ERoadExclusions[];
};

const CatchmentSettingsPopup = ({
  onClose,
  onOpenDatasetTabularModal
}: Props) => {
  const dispatch = useDispatch();
  const { catchmentSettings, catchmentSettingsTemp, spatialConfig } =
    useSelector((state: RootState) => state.spatial);

  const { isDisabled } = useSpatialCatchment();

  const { data: isochroneData } = useGetBulkIsochroneQuery(
    catchmentSettingsTemp,
    {
      enabled: catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD
    }
  );

  const getInvalid = getInvalidContours(
    catchmentSettingsTemp.roadAccessConfig?.isochroneData ?? []
  );

  // Form
  const roadAccessConfig = catchmentSettings?.roadAccessConfig;

  const formHandler = useForm<CatchmentSettingsFormValues>({
    defaultValues: {
      radiusData: !catchmentSettings?.radiusData?.length
        ? [" "]
        : catchmentSettings?.radiusData,
      catchmentType: catchmentSettings?.catchmentType,
      measurementType: roadAccessConfig?.measurementType,
      distances: roadAccessConfig?.distances,
      durations: roadAccessConfig?.durations,
      routingType: roadAccessConfig?.routingType,
      roadExclusions: roadAccessConfig?.roadExclusions
    }
  });

  const calculateAndSaveAreaSize = () => {
    if (
      catchmentSettingsTemp.catchmentType === ECatchmentType.BY_RADIUS &&
      catchmentSettingsTemp.radiusData
    ) {
      const maxRadius = Math.max(
        ...catchmentSettingsTemp.radiusData.map((radius) => parseInt(radius))
      );
      const areaCircle = circle([0, 0], maxRadius, {
        steps: 64,
        units: "meters"
      });

      dispatch(
        setSpatialConfig({
          ...spatialConfig,
          areaSize: area(areaCircle),
          gridResolution: null,
          h3Resolution: null
        })
      );
    } else if (
      catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD &&
      isochroneData
    ) {
      const type =
        roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE
          ? (roadAccessConfig?.distances ?? [""])
          : (roadAccessConfig?.durations ?? [""]);

      const index = getIndexOfHighestValue(type ?? []);
      const highestIsochrone = isochroneData.map(
        (data: { position: number; data: Array<FeatureCollection> }) => {
          return data.data[index];
        }
      );

      const isochroneAreas = highestIsochrone.map((data) => {
        if (data && data.features && data.features[0]?.geometry) {
          const geometry = data.features[0].geometry;
          if (geometry.type === "LineString") {
            const line = lineString(geometry.coordinates);
            const polygon = lineToPolygon(line);
            return area(polygon);
          }
        }
        return 0;
      });

      const maxArea = Math.max(...isochroneAreas);

      dispatch(
        setSpatialConfig({
          ...spatialConfig,
          areaSize: maxArea,
          gridResolution: null,
          h3Resolution: null
        })
      );
    }
  };

  // Save all input data to redux store
  const onSaveSetting = () => {
    calculateAndSaveAreaSize();
    dispatch(setCatchmentSettings(catchmentSettingsTemp));
    onClose();
  };

  useEffect(() => {
    if (isochroneData) {
      dispatch(
        setCatchmentSettingsTempRoad({
          isochroneData
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isochroneData]);

  useEffect(() => {
    dispatch(setCatchmentSettingsTemp(catchmentSettings));
    formHandler.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchmentSettings]);

  return (
    <FormProvider {...formHandler}>
      <Draggable bounds="parent" cancel=".cancel-draggable">
        <div
          className={twMerge(
            "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh]",
            "flex flex-col py-[16px] absolute left-[370px] top-[30px] text-xs" // Add position: absolute
          )}
        >
          <div className="flex flex-row justify-between items-start px-4">
            <div className="flex flex-row items-center gap-2">
              <h2 className="text-txtHeading font-bold text-[20px]">
                Catchment
              </h2>
            </div>
            <button
              className="bg-transparent border-0 p-0"
              onClick={onClose}
              type="button"
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
          {/* Content */}
          <div className="flex flex-col mt-3 custom-scrollbar overflow-y-auto">
            <CatchmentDataSection
              onOpenDatasetTabularModal={onOpenDatasetTabularModal}
            />
            <Divider className="bg-white-smoke w-[100%] h-[8px] min-h-[8px] my-4" />
            <CatchmentTypeSection />
            {/* Save settings */}
            <Button
              className={twMerge(
                "h-[40px] min-h-[40px] mt-3 mx-4 border bg-teal-500 text-white text-sm font-bold",
                "disabled:border-none disabled:bg-gainsboro disabled:text-white"
              )}
              disabled={isDisabled || getInvalid.length > 0}
              onClick={onSaveSetting}
              data-testid={`${TEST_ID_PREFIX}-save-button`}
            >
              <span>Save</span>
            </Button>
          </div>
        </div>
      </Draggable>
    </FormProvider>
  );
};

export default CatchmentSettingsPopup;
